import { Fragment } from "react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { LogOut, Moon, Sun, UserRound } from "lucide-react";
import { useTheme } from "next-themes";

import { LocaleSwitcher } from "@/components/localization/locale-switcher";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useLayout } from "./context";

export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const t = useTranslations("layout30");
  const pathname = usePathname();
  const { settings } = useLayout();

  return (
    <div
      data-slot="sidebar-rail-content"
      data-debug-source="components/layouts/layout-30/components/sidebar-content.tsx"
      className="flex h-full min-h-0 w-full flex-col items-center"
    >
      <nav
        aria-label={t(
          settings.audience === "supplier"
            ? "supplierPrimaryNavigation"
            : "primaryNavigation",
        )}
        className="min-h-0 w-full flex-1"
      >
        <ScrollArea className="h-full min-h-0 w-full">
          <div className="flex shrink-0 flex-col items-center gap-1">
            {settings.primaryMenu.map((item) => {
              const label = t(item.title! as Parameters<typeof t>[0]);
              const selected =
                item.path === pathname ||
                Boolean(
                  item.rootPath &&
                  item.rootPath !== "/" &&
                  pathname.startsWith(`${item.rootPath}/`),
                );
              const icon = item.icon ? (
                <item.icon className="size-4.5!" />
              ) : null;

              return (
                <Fragment key={item.title}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {item.path ? (
                        <Button
                          asChild
                          variant="ghost"
                          mode="icon"
                          data-state={selected ? "open" : undefined}
                          className={cn(
                            "size-9 shrink-0 rounded-md border border-transparent",
                            "hover:text-primary data-[state=open]:border-input",
                          )}
                        >
                          <Link
                            href={item.path as Route}
                            aria-label={label}
                            onClick={onNavigate}
                          >
                            {icon}
                          </Link>
                        </Button>
                      ) : (
                        <Button
                          disabled
                          variant="ghost"
                          mode="icon"
                          aria-label={label}
                          className="size-9 shrink-0 rounded-md border border-transparent"
                        >
                          {icon}
                        </Button>
                      )}
                    </TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                  {item.separator ? <Separator className="my-1 w-6.5" /> : null}
                </Fragment>
              );
            })}
          </div>
        </ScrollArea>
      </nav>
      <SidebarFooter />
    </div>
  );
}

function SidebarFooter() {
  const t = useTranslations("layout30");
  const { resolvedTheme, setTheme } = useTheme();
  const { settings } = useLayout();
  const identity = settings.identity?.initials || t("account.initials");

  return (
    <div className="flex shrink-0 flex-col items-center gap-1 border-t border-border/60 pt-2">
      <LocaleSwitcher variant="rail" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            mode="icon"
            aria-label={t(
              resolvedTheme === "dark"
                ? "theme.switchToLight"
                : "theme.switchToDark",
            )}
            onClick={() =>
              setTheme(resolvedTheme === "dark" ? "light" : "dark")
            }
          >
            {resolvedTheme === "dark" ? (
              <Sun className="size-4.5!" aria-hidden="true" />
            ) : (
              <Moon className="size-4.5!" aria-hidden="true" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {t(
            resolvedTheme === "dark"
              ? "theme.switchToLight"
              : "theme.switchToDark",
          )}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            mode="icon"
            aria-label={t(
              settings.onSignOut ? "account.signOut" : "account.openMenu",
            )}
            disabled={settings.signOutPending}
            onClick={settings.onSignOut}
          >
            {settings.onSignOut ? (
              <LogOut className="size-4.5!" aria-hidden="true" />
            ) : settings.identity ? (
              <span className="text-sm font-semibold">{identity}</span>
            ) : (
              <UserRound className="size-4.5!" aria-hidden="true" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {settings.onSignOut
            ? t("account.signOut")
            : settings.identity?.email || t("account.sessionUnavailable")}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
